import React, { useState, useEffect } from 'react';
import { formatBarrierDateTime } from './dateTimeUtils';
import { BARRIER_TYPES, BARRIER_TYPE_ABBREVIATIONS } from '../../constants';
import { validateWithinEventWindow, TimeRuleContext } from './timeValidation';
import { calculateClosedTotalsByType, calculateDuration } from './barrier-duration';
import BarrierEditPopup, { BarrierFormFields } from './BarrierEditPopup';

/**
 * Represents a single maintenance barrier entry.
 */
export interface MaintenanceBarrier {
  id: string;
  type: string;
  startTime: string;
  endTime: string;
  notes: string;
  defectId?: number;
}

/**
 * Props for the BarriersSection component.
 */
interface BarriersSectionProps {
  barriers: MaintenanceBarrier[];
  setBarriers: React.Dispatch<React.SetStateAction<MaintenanceBarrier[]>>;
  mode: 'view' | 'edit' | 'create';
  isDeaBarriersOpen: boolean;
  setIsDeaBarriersOpen: React.Dispatch<React.SetStateAction<boolean>>;
  defects?: { id: number; text: string }[];
  timeDeclaredZulu?: string;
  returnToServiceTimeZulu?: string;
  /** Whether the event status is Completed (enforces the ERTS ceiling rule). */
  isCompleted?: boolean;
  barrierToEdit?: MaintenanceBarrier | null;
  onBarrierEditHandled?: () => void;
}

/**
 * Maintenance barriers collapsible section with add/edit popup.
 */
const BarriersSection: React.FC<BarriersSectionProps> = ({
  barriers,
  setBarriers,
  mode,
  isDeaBarriersOpen,
  setIsDeaBarriersOpen,
  defects,
  timeDeclaredZulu,
  returnToServiceTimeZulu,
  isCompleted,
  barrierToEdit,
  onBarrierEditHandled,
}) => {
  const [isNewBarrierOpen, setIsNewBarrierOpen] = useState<boolean>(false);
  const [newBarrierType, setNewBarrierType] = useState<string>('');
  const [newBarrierStart, setNewBarrierStart] = useState<string>('');
  const [newBarrierEnd, setNewBarrierEnd] = useState<string>('');
  const [newBarrierNotes, setNewBarrierNotes] = useState<string>('');
  const [newBarrierDefectId, setNewBarrierDefectId] = useState<string>('');
  const [editBarrierId, setEditBarrierId] = useState<string | null>(null);

  const isEditable = mode === 'create' || mode === 'edit';

  // A Completed event may not have an unresolved (open, no end time) barrier.
  // When it does, flag the section as a required field — matching how other
  // required fields are highlighted — so the user closes it before updating.
  const openBarrierRequired =
    isEditable && !!isCompleted && barriers.some((barrier) => !barrier.endTime);

  const closedTotalsByType = calculateClosedTotalsByType(barriers);
  const hasClosedTotals = Object.keys(closedTotalsByType).length > 0;

  // Open edit popup when triggered externally (from DefectsSection open barriers)
  useEffect(() => {
    if (barrierToEdit) {
      handleEditBarrier(barrierToEdit);
      if (onBarrierEditHandled) onBarrierEditHandled();
    }
  }, [barrierToEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  const sortedBarriers = (): MaintenanceBarrier[] => {
    if (!isDeaBarriersOpen) return [];
    // Group first by defect ID, then sort by barrier start date/time within each group
    return [...barriers].sort((a, b) => {
      const defectA = a.defectId ?? Number.MAX_SAFE_INTEGER;
      const defectB = b.defectId ?? Number.MAX_SAFE_INTEGER;
      if (defectA !== defectB) return defectA - defectB;
      return a.startTime.localeCompare(b.startTime);
    });
  };

  /** Returns the display text for a barrier's associated defect (number + text). */
  const getDefectLabel = (defectId?: number): string => {
    if (!defectId) return '—';
    const defect = defects?.find((d) => d.id === defectId);
    return defect ? `${defect.id}) ${defect.text}` : `${defectId})`;
  };

  /** Parses a barrier datetime string (YYYY-MM-DD HH:MM) to a Date object. */
  const parseBarrierDateTime = (value: string): Date | null => {
    if (!value) return null;
    const parts = value.split(' ');
    const date = parts[0] || '';
    const time = parts[1] || '00:00';
    const dt = new Date(`${date}T${time}:00Z`);
    return isNaN(dt.getTime()) ? null : dt;
  };

  /** Validates that barrier times are logically consistent. */
  const isTimeValid = (): boolean => {
    const startDt = parseBarrierDateTime(newBarrierStart);
    if (!startDt) return false;
    // Start time (HH:MM) is required
    const startTime = newBarrierStart.split(' ')[1] || '';
    if (!startTime || startTime.length < 4) return false;
    // If end date is provided, end time is also required
    if (newBarrierEnd) {
      const endTime = newBarrierEnd.split(' ')[1] || '';
      if (!endTime || endTime.length < 4) return false;
      const endDt = parseBarrierDateTime(newBarrierEnd);
      if (endDt && endDt < startDt) return false;
    }
    return true;
  };

  // Cross-field temporal rules for the in-progress barrier: start/end must be
  // later than Time Declared and (once Completed) not after the ERTS. Only
  // applied to a complete HH:MM value so partial typing isn't flagged early.
  const barrierTimeContext: TimeRuleContext = {
    timeDeclared: timeDeclaredZulu,
    erts: returnToServiceTimeZulu,
    isCompleted: !!isCompleted,
  };
  const hasFullTime = (value: string): boolean => (value.split(' ')[1] || '').length >= 4;
  const barrierTimeError = ((): string | null => {
    if (newBarrierStart && hasFullTime(newBarrierStart)) {
      // Start time may equal the Time Declared (a barrier can begin at the
      // moment the event was declared), so allow equality here.
      const startError = validateWithinEventWindow(newBarrierStart, barrierTimeContext, {
        allowEqualToTimeDeclared: true,
      });
      if (startError) return `Start Time: ${startError}`;
    }
    if (newBarrierEnd && hasFullTime(newBarrierEnd)) {
      const endError = validateWithinEventWindow(newBarrierEnd, barrierTimeContext);
      if (endError) return `End Time: ${endError}`;
    }
    return null;
  })();

  /** Clears every field in the add/edit popup back to its empty default. */
  const resetBarrierForm = (): void => {
    setEditBarrierId(null);
    setNewBarrierType('');
    setNewBarrierStart('');
    setNewBarrierEnd('');
    setNewBarrierNotes('');
    setNewBarrierDefectId('');
  };

  /** Routes a popup field change to the matching piece of form state. */
  const updateBarrierFields = (patch: Partial<BarrierFormFields>): void => {
    if (patch.defectId !== undefined) setNewBarrierDefectId(patch.defectId);
    if (patch.type !== undefined) setNewBarrierType(patch.type);
    if (patch.start !== undefined) setNewBarrierStart(patch.start);
    if (patch.end !== undefined) setNewBarrierEnd(patch.end);
    if (patch.notes !== undefined) setNewBarrierNotes(patch.notes);
  };

  /** Closes the popup and discards any in-progress values. */
  const handleCancelBarrierEdit = (): void => {
    setIsNewBarrierOpen(false);
    resetBarrierForm();
  };

  const handleAddBarrier = (): void => {
    if (editBarrierId) {
      setBarriers((prev) =>
        prev.map((b) =>
          b.id === editBarrierId
            ? {
                ...b,
                type: newBarrierType,
                startTime: newBarrierStart,
                endTime: newBarrierEnd,
                notes: newBarrierNotes,
                defectId: newBarrierDefectId ? Number(newBarrierDefectId) : undefined,
              }
            : b,
        ),
      );
      setEditBarrierId(null);
    } else {
      const newBarrier: MaintenanceBarrier = {
        id: `barrier-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`,
        type: newBarrierType,
        startTime: newBarrierStart,
        endTime: newBarrierEnd,
        notes: newBarrierNotes,
        defectId: newBarrierDefectId ? Number(newBarrierDefectId) : undefined,
      };
      setBarriers((prev) => [...prev, newBarrier]);
    }
    resetBarrierForm();
    setIsNewBarrierOpen(false);
  };

  const handleEditBarrier = (barrier: MaintenanceBarrier): void => {
    setEditBarrierId(barrier.id);
    setNewBarrierType(barrier.type);
    setNewBarrierStart(barrier.startTime);
    setNewBarrierEnd(barrier.endTime);
    setNewBarrierNotes(barrier.notes);
    setNewBarrierDefectId(barrier.defectId ? String(barrier.defectId) : '');
    setIsNewBarrierOpen(true);
  };

  /** Removes a barrier from the list. On save the backend soft-deletes any
   * barrier no longer present, so history is preserved server-side. */
  const handleDeleteBarrier = (barrier: MaintenanceBarrier): void => {
    setBarriers((prev) => prev.filter((b) => b.id !== barrier.id));
  };

  return (
    <>
      <div className="collapsible-section">
        <div className="barriers-header">
          <button
            className={`collapsible-toggle${openBarrierRequired ? ' collapsible-toggle-required' : ''}`}
            type="button"
            onClick={() => setIsDeaBarriersOpen(!isDeaBarriersOpen)}
          >
            <span className={`toggle-arrow ${isDeaBarriersOpen ? 'open' : ''}`}>&#9656;</span>
            Maintenance Barriers
          </button>
          {isEditable && (
            <button
              className="barrier-add-btn"
              type="button"
              onClick={() => {
                // Start every new barrier from a clean slate so no values leak
                // in from a prior add or a cancelled edit.
                resetBarrierForm();
                // Auto-populate the Associated Defect when there's exactly one
                // defect; with multiple defects, require an explicit selection.
                if (defects && defects.length === 1) {
                  setNewBarrierDefectId(String(defects[0].id));
                }
                setIsNewBarrierOpen(true);
              }}
              aria-label="Add new barrier"
            >
              +
            </button>
          )}
        </div>
        {sortedBarriers().length > 0 && (
          <div className="barriers-list">
            {sortedBarriers().map((barrier, index, arr) => {
              const prevBarrier = index > 0 ? arr[index - 1] : null;
              const isFirstForDefect = !prevBarrier || prevBarrier.defectId !== barrier.defectId;
              return (
                <React.Fragment key={barrier.id}>
                  {isFirstForDefect && (
                    <div className="barrier-defect-group-label">
                      {getDefectLabel(barrier.defectId)}
                    </div>
                  )}
                  <div
                    className={`barrier-open-row${openBarrierRequired && !barrier.endTime ? ' barrier-open-row-required' : ''}`}
                  >
                    <span className="barrier-open-type">{barrier.type}</span>
                    <span className="barrier-open-start">
                      {formatBarrierDateTime(barrier.startTime)}
                    </span>
                    <span className="barrier-duration">{calculateDuration(barrier)}h</span>
                    {barrier.notes && <span className="barrier-open-notes">{barrier.notes}</span>}
                    {isEditable && (
                      <button
                        className="barrier-edit-btn"
                        type="button"
                        onClick={() => handleEditBarrier(barrier)}
                        aria-label="Edit barrier"
                      >
                        &#9998;
                      </button>
                    )}
                    {isEditable && (
                      <button
                        className="barrier-delete-btn"
                        type="button"
                        onClick={() => handleDeleteBarrier(barrier)}
                        aria-label="Delete barrier"
                        title="Delete barrier"
                      >
                        &#128465;
                      </button>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        )}
        {isDeaBarriersOpen && hasClosedTotals && (
          <div className="barrier-totals">
            <span className="barrier-totals-label">Total Open Time by Barrier</span>
            <div className="barrier-totals-row">
              {BARRIER_TYPES.filter((type) => closedTotalsByType[type] !== undefined).map(
                (type) => (
                  <span key={type} className="barrier-total-item">
                    <span className="barrier-total-abbr">{BARRIER_TYPE_ABBREVIATIONS[type]}</span>
                    <span className="barrier-total-hours">
                      {closedTotalsByType[type].toFixed(2)}h
                    </span>
                  </span>
                ),
              )}
            </div>
          </div>
        )}
      </div>

      {isNewBarrierOpen && (
        <BarrierEditPopup
          isEditing={!!editBarrierId}
          fields={{
            defectId: newBarrierDefectId,
            type: newBarrierType,
            start: newBarrierStart,
            end: newBarrierEnd,
            notes: newBarrierNotes,
          }}
          updateFields={updateBarrierFields}
          defects={defects}
          timeError={barrierTimeError}
          canSubmit={
            !!newBarrierType &&
            !!newBarrierStart &&
            !!newBarrierDefectId &&
            isTimeValid() &&
            !barrierTimeError
          }
          onCancel={handleCancelBarrierEdit}
          onSubmit={handleAddBarrier}
        />
      )}
    </>
  );
};

export default BarriersSection;
